/**
 * Раздел «Запросы в NetBox».
 *
 * Очередь контролёра данных, список объектов, готовых к отправке, —
 * для координатора, и история. Карточка запроса показывает ровно то,
 * что уйдёт в NetBox, комментарии участников и итог отправки.
 *
 * Список перечитывается сам по событиям сервера: запрос, созданный или
 * одобренный в другом окне, появляется без перезагрузки страницы.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router';

import { api } from '../../api/client';
import { selectUser } from '../../store/slices/authSlice';
import { selectPrefixMap } from '../../store/slices/switchesSlice';
import { selectNetboxCounts, selectNetboxVersion, refreshNetbox } from '../../store/slices/netboxSlice';
import { objectLabel } from '../../domain/issues';
import {
  REQUEST_STATUS, statusLabel, describeCounts, formatDateTime,
  canDecide, canRetry, canWithdraw, canCreateRequest, canMarkReady, canSeeRequests,
} from '../../domain/netboxRequests';
import NetboxExport from '../objects/NetboxExport';
import styles from './NetboxRequestsPage.module.css';

export default function NetboxRequestsPage() {
  const { id } = useParams();
  const user = useSelector(selectUser);
  // Раздела нет в меню у редактора и «Просмотра» — прямая ссылка тоже не открывает
  if (!canSeeRequests(user)) return <Navigate to="/dashboard" replace />;
  return id ? <RequestDetail id={id} /> : <RequestList />;
}

/** Плашка состояния. */
function StatusPill({ status }) {
  const tone = REQUEST_STATUS[status]?.tone || 'muted';
  return <span className={`${styles.pill} ${styles[`tone_${tone}`]}`}>{statusLabel(status)}</span>;
}

const who = (name, login) => name || login || '—';

// ── Список ────────────────────────────────────────────────────────────────

function RequestList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const prefixMap = useSelector(selectPrefixMap);
  const counts = useSelector(selectNetboxCounts);
  const version = useSelector(selectNetboxVersion);

  const [tab, setTab] = useState(() => (user?.role === 'coordinator' && counts.ready > 0 ? 'ready' : 'open'));
  const [requests, setRequests] = useState({ state: 'loading', rows: [] });
  const [ready, setReady] = useState([]);
  const [exportMag, setExportMag] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, readyList] = await Promise.all([
        tab === 'ready' ? Promise.resolve(null) : api.get(`/api/netbox/requests?status=${tab}`),
        api.get('/api/netbox/ready'),
      ]);
      if (list) setRequests({ state: 'ready', rows: list.requests });
      setReady(readyList.ready);
    } catch (e) {
      setError(e.message);
      setRequests((r) => ({ ...r, state: 'ready' }));
    }
  }, [tab]);

  useEffect(() => { load(); }, [load, version]);

  async function unmark(mag) {
    try {
      await api.delete(`/api/netbox/ready/${encodeURIComponent(mag)}`);
      dispatch(refreshNetbox());
    } catch (e) {
      setError(e.message);
    }
  }

  const label = (mag) => objectLabel(mag, prefixMap);
  const role = user?.role;
  const duty = {
    controller: 'Ваша роль — контролёр данных: проверьте запрос и одобрите или отклоните его. После одобрения данные сразу уходят в NetBox.',
    coordinator: 'Ваша роль — координатор: создайте запрос по объекту, готовому к отправке. Его проверит контролёр данных.',
    developer: 'Отправка в NetBox идёт через согласование: координатор создаёт запрос, контролёр данных одобряет.',
  }[role];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Запросы в NetBox</h1>
        <p className={styles.subtitle}>Данные уходят в NetBox только после проверки — в три шага.</p>
      </div>

      <ol className={styles.steps}>
        {FLOW.map((step) => (
          <li key={step.role} className={`${styles.step} ${step.role === role ? styles.stepMine : ''}`}>
            <span className={styles.stepNum} aria-hidden="true">{step.n}</span>
            <span className={styles.stepText}>
              <span className={styles.stepTitle}>{step.title}</span>
              <span className={styles.stepWho}>{step.who}{step.role === role ? ' — это вы' : ''}</span>
            </span>
          </li>
        ))}
      </ol>
      {duty && <div className={styles.duty}>{duty}</div>}

      <div className={styles.tabs}>
        <button className={`${styles.tab} ${tab === 'open' ? styles.tabActive : ''}`} onClick={() => setTab('open')}>
          На проверке <span className={styles.count}>{counts.pending}</span>
        </button>
        <button className={`${styles.tab} ${tab === 'ready' ? styles.tabActive : ''}`} onClick={() => setTab('ready')}>
          Готовы к отправке <span className={styles.count}>{counts.ready}</span>
        </button>
        <button className={`${styles.tab} ${tab === 'closed' ? styles.tabActive : ''}`} onClick={() => setTab('closed')}>
          История
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {tab === 'ready' ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>Объект</th><th>Отметил</th><th>Когда</th><th /></tr>
            </thead>
            <tbody>
              {ready.map((r) => (
                <tr key={r.mag}>
                  <td className={styles.strong}>{label(r.mag)}</td>
                  <td>{who(r.markedByName, r.markedBy)}</td>
                  <td className={styles.muted}>{formatDateTime(r.markedAt)}</td>
                  <td className={styles.actions}>
                    <button className={styles.button} onClick={() => navigate(`/objects/${r.mag}`)}>Открыть объект</button>
                    {canCreateRequest(user) && (
                      <button className={`${styles.button} ${styles.primary}`} onClick={() => setExportMag(r.mag)}>
                        Создать запрос
                      </button>
                    )}
                    {canMarkReady(user) && (
                      <button className={styles.button} onClick={() => unmark(r.mag)}>Снять отметку</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ready.length === 0 && <div className={styles.none}>Готовых к отправке объектов нет.</div>}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>№</th><th>Объект</th><th className={styles.hideMobile}>Состав</th><th>Создал</th>
                <th className={styles.hideMobile}>Создан</th>{tab === 'closed' && <th className={styles.hideMobile}>Решение</th>}<th>Состояние</th>
              </tr>
            </thead>
            <tbody>
              {requests.rows.map((r) => (
                <tr key={r.id} className={styles.row} onClick={() => navigate(`/netbox/${r.id}`)}>
                  <td className={styles.num}>{r.id}</td>
                  <td className={styles.strong}>{label(r.mag)}</td>
                  <td className={`${styles.hideMobile} ${styles.muted}`}>{describeCounts(r.counts)}</td>
                  <td>{who(r.createdByName, r.createdBy)}</td>
                  <td className={`${styles.hideMobile} ${styles.muted}`}>{formatDateTime(r.createdAt)}</td>
                  {tab === 'closed' && (
                    <td className={`${styles.hideMobile} ${styles.muted}`}>
                      {r.decidedBy ? `${who(r.decidedByName, r.decidedBy)}, ${formatDateTime(r.decidedAt)}` : '—'}
                    </td>
                  )}
                  <td><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {requests.state === 'loading' && <div className={styles.none}>Загружаю…</div>}
          {requests.state === 'ready' && requests.rows.length === 0 && (
            <div className={styles.none}>{tab === 'open' ? 'Запросов на проверке нет.' : 'История пуста.'}</div>
          )}
        </div>
      )}

      {exportMag && (
        <NetboxExport mag={exportMag} objectLabel={label(exportMag)} onClose={() => { setExportMag(null); load(); }} />
      )}
    </div>
  );
}

/** Шаги согласования — по ним видно, где в цепочке пользователь. */
const FLOW = [
  { n: 1, role: 'admin', title: 'Готово к отправке', who: 'Редактор заполняет объект и отмечает его' },
  { n: 2, role: 'coordinator', title: 'Запрос', who: 'Координатор создаёт запрос с комментарием' },
  { n: 3, role: 'controller', title: 'Проверка', who: 'Контролёр данных одобряет — данные уходят в NetBox' },
];

// ── Карточка запроса ──────────────────────────────────────────────────────

function RequestDetail({ id }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const prefixMap = useSelector(selectPrefixMap);
  const version = useSelector(selectNetboxVersion);

  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [comment, setComment] = useState('');
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setRequest(await api.get(`/api/netbox/requests/${encodeURIComponent(id)}`));
    } catch (e) {
      setError(e.message);
    }
  }, [id]);

  // Пока идёт собственное действие, события не перечитывают карточку:
  // иначе ответ сервера и перечитывание спорили бы, что показать.
  useEffect(() => { if (!busy) load(); }, [load, version, busy]);

  async function act(kind, path, body, question) {
    if (question && !window.confirm(question)) return;
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.post(`/api/netbox/requests/${request.id}/${path}`, body);
      setRequest(updated);
      setComment('');
      if (updated.status === 'sent') setNotice('Данные отправлены в NetBox.');
      if (updated.status === 'partial') setNotice('Часть позиций NetBox не принял — подробности ниже.');
    } catch (e) {
      setError(e.message);
      await load();
    } finally {
      setBusy(null);
      dispatch(refreshNetbox());
    }
  }

  if (!request) {
    return (
      <div className={styles.page}>
        <button className={styles.back} onClick={() => navigate('/netbox')}>← Все запросы</button>
        {error ? <div className={styles.error}>{error}</div> : <div className={styles.none}>Загружаю…</div>}
      </div>
    );
  }

  const label = objectLabel(request.mag, prefixMap);
  const decide = canDecide(user, request);
  const retry = canRetry(user, request);
  const withdraw = canWithdraw(user, request);
  const ownForController = user?.role === 'controller' && request.createdBy === user.username;
  const canRecreate = canCreateRequest(user) && ['rejected', 'stale', 'withdrawn'].includes(request.status);
  const snap = request.snapshot || {};
  const attempts = request.result?.attempts || [];
  const diff = request.result?.diff;
  const decisionTitle = { sent: 'Одобрен', partial: 'Одобрен', sending: 'Одобрен', rejected: 'Отклонён', stale: 'Не одобрен — устарел' }[request.status];

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate('/netbox')}>← Все запросы</button>

      <div className={styles.detailHead}>
        <div>
          <h1 className={styles.title}>Запрос №{request.id}, {label}</h1>
          <div className={styles.subtitle}>{describeCounts(request.counts)}</div>
        </div>
        <StatusPill status={request.status} />
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {notice && <div className={styles.ok}>{notice}</div>}

      <div className={styles.timeline}>
        <div className={styles.event}>
          <div className={styles.eventHead}>
            <b>Создан</b> · {who(request.createdByName, request.createdBy)} · {formatDateTime(request.createdAt)}
          </div>
          {request.comment && <div className={styles.quote}>{request.comment}</div>}
        </div>

        {request.status === 'withdrawn' && (
          <div className={styles.event}>
            <div className={styles.eventHead}><b>Отозван автором</b> · {formatDateTime(request.updatedAt)}</div>
          </div>
        )}

        {request.decidedBy && (
          <div className={styles.event}>
            <div className={styles.eventHead}>
              <b>{decisionTitle}</b> · {who(request.decidedByName, request.decidedBy)} · {formatDateTime(request.decidedAt)}
            </div>
            {request.decisionComment && <div className={styles.quote}>{request.decisionComment}</div>}
            {diff && (
              <div className={styles.diff}>
                {diff.changed?.length > 0 && <div>Изменились: {diff.changed.join(', ')}</div>}
                {diff.added?.length > 0 && <div>Появились: {diff.added.join(', ')}</div>}
                {diff.removed?.length > 0 && <div>Больше не нужны: {diff.removed.join(', ')}</div>}
                {request.result?.siteProblem && <div>{request.result.siteProblem}</div>}
              </div>
            )}
          </div>
        )}

        {request.result?.interrupted && (
          <div className={styles.event}>
            <div className={styles.eventHead}><b>Отправка прервана</b> — сервер перезапускался. Повторная отправка не задвоит уже выполненное.</div>
          </div>
        )}

        {attempts.map((a, i) => (
          <div key={`${a.at}-${i}`} className={styles.event}>
            <div className={styles.eventHead}>
              <b>{i === 0 ? 'Отправка' : 'Повторная отправка'}</b> · {formatDateTime(a.at)} · отправлено {a.sent}
              {a.failed.length > 0 && <>, не принято {a.failed.length}</>}
            </div>
            {a.failed.length > 0 && (
              <ul className={styles.errors}>
                {a.failed.map((f, j) => <li key={j}><span className={styles.mono}>{f.объект}</span> — {f.ошибка}</li>)}
              </ul>
            )}
            {a.excluded?.length > 0 && (
              <div className={styles.diff}>
                Изменились после одобрения и не отправлены: {a.excluded.join(', ')}. Для них нужен новый запрос.
              </div>
            )}
          </div>
        ))}
      </div>

      <SnapshotView snap={snap} />

      {(decide || retry || withdraw || ownForController || canRecreate) && (
        <div className={styles.actionBar}>
          {decide && (
            <>
              <textarea
                className={styles.comment}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Комментарий — обязателен при отклонении"
                rows={2}
                maxLength={2000}
                aria-label="Комментарий к решению"
              />
              <div className={styles.actionRow}>
                <span className={styles.muted}>Перед отправкой сервер сверит данные: если они изменились, запрос устареет.</span>
                <div className={styles.spacer} />
                <button className={`${styles.button} ${styles.danger}`} disabled={Boolean(busy) || !comment.trim()}
                  title={comment.trim() ? '' : 'Укажите причину в комментарии'}
                  onClick={() => act('reject', 'reject', { comment })}>
                  Отклонить
                </button>
                <button className={`${styles.button} ${styles.primary}`} disabled={Boolean(busy)}
                  onClick={() => act('approve', 'approve', { comment },
                    `Одобрить запрос №${request.id} и отправить в NetBox?\n\n${label}: ${describeCounts(request.counts)}`)}>
                  {busy === 'approve' ? 'Отправляю в NetBox…' : 'Одобрить и отправить'}
                </button>
              </div>
            </>
          )}
          {retry && (
            <div className={styles.actionRow}>
              <span className={styles.muted}>Повторно уйдут только непринятые позиции — уже отправленное не задвоится.</span>
              <div className={styles.spacer} />
              <button className={`${styles.button} ${styles.primary}`} disabled={Boolean(busy)}
                onClick={() => act('retry', 'retry', {})}>
                {busy === 'retry' ? 'Отправляю…' : 'Повторить отправку'}
              </button>
            </div>
          )}
          {withdraw && (
            <div className={styles.actionRow}>
              <span className={styles.muted}>Пока запрос не проверен, его можно отозвать — например, чтобы поправить данные.</span>
              <div className={styles.spacer} />
              <button className={styles.button} disabled={Boolean(busy)}
                onClick={() => act('withdraw', 'withdraw', {}, `Отозвать запрос №${request.id}?`)}>
                Отозвать запрос
              </button>
            </div>
          )}
          {ownForController && (request.status === 'pending' || request.status === 'partial') && (
            <div className={styles.actionRow}>
              <span className={styles.muted}>Это ваш запрос — решение по нему принимает другой контролёр.</span>
            </div>
          )}
          {canRecreate && (
            <div className={styles.actionRow}>
              <span className={styles.muted}>Исправьте данные объекта и создайте новый запрос.</span>
              <div className={styles.spacer} />
              <button className={styles.button} onClick={() => navigate(`/objects/${request.mag}`)}>Открыть объект</button>
              <button className={`${styles.button} ${styles.primary}`} onClick={() => setExportOpen(true)}>Новый запрос</button>
            </div>
          )}
        </div>
      )}

      {exportOpen && <NetboxExport mag={request.mag} objectLabel={label} onClose={() => setExportOpen(false)} />}
    </div>
  );
}

/** Что уйдёт в NetBox — снимок запроса. */
function SnapshotView({ snap }) {
  const serials = snap.serials || [];
  const racks = snap.racks || { create: [], placements: [], moves: [] };
  const devices = snap.devices || [];
  return (
    <div className={styles.snapshot}>
      {serials.length > 0 && (
        <Section title="Серийные номера" note={serials.some((x) => x.overwrites) ? 'Выделенные строки — перезапись значений, внесённых в NetBox ранее' : null}>
          <Table head={['Коммутатор', 'Было в NetBox', 'Станет']}>
            {serials.map((x) => (
              <tr key={x.key} className={x.overwrites ? styles.rowWarn : ''}>
                <td className={styles.mono}>{x.sw}</td>
                <td className={styles.mono}>{x.oldSerial ?? <span className={styles.muted}>пусто</span>}</td>
                <td className={styles.mono}>{x.newSerial}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
      {racks.create.length > 0 && (
        <Section title="Новые стойки">
          <Table head={['Стойка', 'Высота']}>
            {racks.create.map((r) => (
              <tr key={r.key}><td>{r.name}</td><td>{r.height ? `${r.height}U` : <span className={styles.muted}>не указана</span>}</td></tr>
            ))}
          </Table>
        </Section>
      )}
      {racks.placements.length > 0 && (
        <Section title="Размещение">
          <Table head={['Коммутатор', 'Стойка', 'Юнит', 'Было']}>
            {racks.placements.map((x) => (
              <tr key={x.key}>
                <td className={styles.mono}>{x.sw}</td><td>{x.rack}</td><td>{x.position}</td>
                <td>{x.currentPosition != null ? `юнит ${x.currentPosition}` : <span className={styles.muted}>не размещён</span>}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
      {racks.moves.length > 0 && (
        <Section title="Переносы между стойками" note="Перенос — изменение размещения, внесённого в NetBox ранее">
          <Table head={['Коммутатор', 'Из стойки', 'В стойку', 'Юнит']}>
            {racks.moves.map((x) => (
              <tr key={x.key} className={styles.rowWarn}>
                <td className={styles.mono}>{x.sw}</td><td>{x.currentRack}</td><td>{x.rack}</td><td>{x.position}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
      {devices.length > 0 && (
        <Section title="Новые устройства">
          <Table head={['Коммутатор', 'Тип в NetBox', 'Серийный номер']}>
            {devices.map((x) => (
              <tr key={x.key}>
                <td className={styles.mono}>{x.sw}</td><td>{x.deviceTypeName}</td>
                <td className={styles.mono}>{x.serial ?? <span className={styles.muted}>нет</span>}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {children}
      {note && <div className={styles.sectionNote}>{note}</div>}
    </section>
  );
}

function Table({ head, children }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
